import React, { useState, useEffect } from "react";
import { Row, Col, Alert } from "reactstrap";
import { useFormContext } from "react-hook-form";
import withStyles from "isomorphic-style-loader/withStyles";
import * as yup from "yup";
import s from "./styles.scss";
import WizardFormStep from "../../components/WizardFormStep";
import { fetchBrandAssets } from "../../../../../../utils/fetch";
import { extractDomain } from "../../../../../../utils/validation/validationUtils";
import { BrandAssetsResponse, BrandAssetsSelection } from "../types";
import { serviceCaller, ServiceCallerProps } from "../../../../../composition";
import AssetSection from "./components/AssetSection";
import WizardFooter from "../../../../../../toolkit/wizardFooter";
import { useLoading } from "../../../../../../hooks/useLoading";
import { saveBrandSetupData } from "../../../../../../utils/firebase/brandSetup";
import { ensureCustomizationData } from "../../../../../../utils/firebase/customization";
import {
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  YouTube as YouTubeIcon,
  Language as WebsiteIcon
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import LoadingSpinner from "../../../../../../toolkit/loadingSpinner";

export const brandAssetsSelectionSchema = yup.object({
  useBrandName: yup.boolean(),
  useDescription: yup.boolean(),
  useLogos: yup.boolean(),
  useFavicon: yup.boolean(),
  useColors: yup.boolean(),
  useLinks: yup.boolean(),
  useFonts: yup.boolean().optional(),
  useImages: yup.boolean().optional(),
});

interface BrandAssetsSelectionProps extends ServiceCallerProps {}

const BrandAssetsSelection: React.FC<BrandAssetsSelectionProps> = ({
  config,
  serviceAccessToken,
  currentCharity,
}) => {
  const getLinkIcon = (linkName: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      twitter: <TwitterIcon fontSize="small" />,
      linkedin: <LinkedInIcon fontSize="small" />,
      instagram: <InstagramIcon fontSize="small" />,
      facebook: <FacebookIcon fontSize="small" />,
      youtube: <YouTubeIcon fontSize="small" />,
      website: <WebsiteIcon fontSize="small" />,
    };

    return iconMap[linkName.toLowerCase()] || <WebsiteIcon fontSize="small" />;
  };
  const { watch, getValues } = useFormContext();
  const brandUrl = watch("brandUrl");
  const brandSetupMethod = watch("brandSetupMethod");
  const [brandData, setBrandData] = useState<BrandAssetsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { loading: isFetchingAssets, runWithLoading: fetchAssetsWithLoading } = useLoading(false);

  useEffect(() => {
    if (!brandUrl || !config?.adminServiceUrl || !serviceAccessToken || !currentCharity?.id) {
      return;
    }

    if (brandSetupMethod !== "fetch") {
      return;
    }

    if (brandData) {
      return;
    }

    const fetchData = async () => {
      setError(null);
      try {
        const cleanDomain = extractDomain(brandUrl);
        const data = await fetchAssetsWithLoading(async () => {
          return await fetchBrandAssets(
            config.adminServiceUrl,
            currentCharity?.id || "",
            cleanDomain,
            serviceAccessToken
          );
        }, 1000);
        setBrandData(data);
      } catch (err) {
        setError("Failed to fetch brand assets. Please check the URL and try again.");
      }
    };

    fetchData();
  }, [
    brandUrl,
    brandSetupMethod,
    config?.adminServiceUrl,
    currentCharity?.id,
    serviceAccessToken,
    fetchAssetsWithLoading,
    brandData,
  ]);

  const renderErrorState = () => (
    <Alert color="danger">
      <h5>Unable to fetch brand assets</h5>
      <p>{error}</p>
      <p>
        You can go back to try a different URL or continue with manual setup.
      </p>
    </Alert>
  );

  const renderManualSetupState = () => (
    <Alert color="info">
      <h5>Manual Setup Selected</h5>
      <p>
        You chose to upload files manually. You can proceed to the next step to
        customize your brand assets.
      </p>
    </Alert>
  );

  const renderSelectionInterface = () => {
    if (!brandData) return null;

    return (
      <div className={s.selectionContainer}>
        <div className={`${s.sectionsContainer}`}>
          <Row>
            <Col md={12}>
              <AssetSection
                title="Organization Name"
                fieldName="useBrandName"
                variant="row"
                preview={brandData.name}
                previewClassName={s.brandNameText}
              />
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <AssetSection
                title="Mission Statement"
                fieldName="useDescription"
                preview={brandData.description}
              />
            </Col>
            <Col md={6}>
              <AssetSection
                title="Logo Files"
                fieldName="useLogos"
                preview={`${brandData.logos.filter(logo => logo.type === "logo").length} logo(s) found`}
                customPreview={
                  <div className={s.logoPreview}>
                    {brandData.logos
                      .filter(logo => logo.type === "logo")
                      .map((logo, index) => (
                      <img
                        key={index}
                        src={logo.formats[0]?.src}
                        alt="Logo preview"
                        className={s.logoImage}
                      />
                    ))}
                  </div>
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <AssetSection
                title="Favicon"
                fieldName="useFavicon"
                preview={`${brandData.logos.filter(logo => logo.type === "icon" || logo.type === "symbol").length} icon(s) found`}
                customPreview={
                  <div className={s.logoPreview}>
                    {brandData.logos
                      .filter(logo => logo.type === "icon" || logo.type === "symbol")
                      .map((logo, index) => (
                      <img
                        key={index}
                        src={logo.formats[0]?.src}
                        alt="Favicon preview"
                        className={s.logoImage}
                      />
                    ))}
                  </div>
                }
              />
            </Col>
            <Col md={6}>
              <AssetSection
                title="Brand Colors"
                fieldName="useColors"
                variant="row"
                preview={`${brandData.colors.length} color(s) found`}
                customPreview={
                  <div className={s.colorPreview}>
                    {brandData.colors.slice(0, 4).map((color, index) => (
                      <Tooltip
                        key={index}
                        title={`${color.hex} (${color.type})`}
                        arrow
                      >
                        <div
                          className={s.colorSwatch}
                          style={{ backgroundColor: color.hex }}
                        />
                      </Tooltip>
                    ))}
                  </div>
                }
              />
            </Col>
            <Col md={6}>
              <AssetSection
                title="Social & Website Links"
                fieldName="useLinks"
                variant="row"
                preview={`${brandData.links.length} link(s) found`}
                customPreview={
                  <div className={s.linksPreview}>
                    {brandData.links.slice(0, 4).map((link, index) => (
                      <Tooltip key={index} title={link.url} arrow>
                        <div className={s.linkIcon}>
                          {getLinkIcon(link.name)}
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                }
              />
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  const handleNext = async (formData: any) => {
    if (!currentCharity?.id) {
      console.error("No charity ID available");
      return;
    }

    try {
      if (brandSetupMethod === "manual") {
        return;
      }

      const selectedAssets = {
        useBrandName: formData.useBrandName || false,
        useDescription: formData.useDescription || false,
        useLogos: formData.useLogos || false,
        useFavicon: formData.useFavicon || false,
        useColors: formData.useColors || false,
        useLinks: formData.useLinks || false,
        useFonts: formData.useFonts || false,
        useImages: formData.useImages || false,
      };

      await saveBrandSetupData(currentCharity.id, {
        brandfetchResponse: brandData,
        selectedAssets,
        progressTracker: "assets_selected",
      });

      await ensureCustomizationData(currentCharity.id, { replaceExisting: true });
    } catch (error) {
      console.error("Failed to save brand assets selection:", error);
      throw error;
    }
  };

  const renderContent = () => {
    if (brandSetupMethod === "manual") {
      return renderManualSetupState();
    }


    if (error) {
      return renderErrorState();
    }

    return renderSelectionInterface();
  };

  if (isFetchingAssets) {
    return (
      <Row className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <LoadingSpinner
          title="Fetching brand assets..."
          message="Please wait while we retrieve assets from the provided URL."
        />
      </Row>
    )
  }

  return (
    <WizardFormStep
      title="Select Your Brand Assets"
      subtitle="Choose which assets we fetched to include in your site and marketing."
      className={s.brandAssetsContainer}
    >
      <Row className={`justify-content-center`}>
        <Col md={9}>
          {renderContent()}
        </Col>
      </Row>

      <WizardFooter
        schema={brandAssetsSelectionSchema}
        loading={isFetchingAssets}
        loadingText="Fetching assets..."
        onNext={handleNext}
      />
    </WizardFormStep>
  );
};

export default serviceCaller(withStyles(s)(BrandAssetsSelection));
